/** Password reset request and confirmation screens backed by allauth headless. */
import { Link, useParams } from "@tanstack/react-router";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { AuthError, requestPasswordReset, resetPasswordFromKey } from "@/api/auth";
import { LanguageSwitcher } from "@/app/shell/AppShell";
import { SmartWatersMark, ThemeToggle } from "@/shared/ui/brand";
import { Button } from "@/shared/ui/button";
import { Field, Input } from "@/shared/ui/form";

/** Request a password-reset email (`/auth/reset-password`). */
export function ResetPasswordRequestPage(): ReactElement {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: { preventDefault(): void }): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      setError(err instanceof AuthError ? err.message : t("app.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm overflow-hidden rounded-lg border border-t-3 border-t-brand bg-card shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-sidebar px-5 py-3.5">
          <div className="flex min-w-0 items-center gap-2.5">
            <SmartWatersMark className="size-7" />
            <div>
              <div className="font-bold tracking-tight text-foreground">{t("app.brand")}</div>
              <div className="text-xs text-muted-foreground">{t("auth.resetPasswordTitle")}</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <ThemeToggle />
            <LanguageSwitcher />
          </div>
        </div>
        <div className="space-y-4 p-5">
          {sent ? (
            <div className="space-y-3 text-xs">
              <p className="text-foreground">{t("auth.resetEmailSent")}</p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/login">{t("auth.backToSignIn")}</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={(e) => void submit(e)} className="space-y-3.5" noValidate>
              <p className="text-xs text-muted-foreground">{t("auth.resetPasswordHint")}</p>
              <Field id="reset-email" label={t("auth.email")}>
                <Input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </Field>
              {error ? (
                <p role="alert" className="text-xs text-destructive">
                  {error}
                </p>
              ) : null}
              <Button type="submit" className="w-full" disabled={busy || !email.trim()}>
                {busy ? t("app.saving") : t("auth.sendResetLink")}
              </Button>
              <div className="text-center">
                <Link
                  to="/login"
                  className="text-xs font-medium text-brand-foreground underline underline-offset-4 hover:text-primary"
                >
                  {t("auth.backToSignIn")}
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/** Set a new password using a reset token (`/auth/reset-password/$key`). */
export function ResetPasswordConfirmPage(): ReactElement {
  const { t } = useTranslation();
  const params: { key?: string } = useParams({ strict: false });
  const key = params.key;
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: { preventDefault(): void }): Promise<void> => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError(t("auth.passwordsDoNotMatch"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await resetPasswordFromKey(key ?? "", password);
      setDone(true);
    } catch (err) {
      setError(err instanceof AuthError ? err.message : t("app.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm overflow-hidden rounded-lg border border-t-3 border-t-brand bg-card shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-sidebar px-5 py-3.5">
          <div className="flex min-w-0 items-center gap-2.5">
            <SmartWatersMark className="size-7" />
            <div>
              <div className="font-bold tracking-tight text-foreground">{t("app.brand")}</div>
              <div className="text-xs text-muted-foreground">{t("auth.setNewPassword")}</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <ThemeToggle />
            <LanguageSwitcher />
          </div>
        </div>
        <div className="space-y-4 p-5">
          {done ? (
            <div className="space-y-3 text-xs">
              <p className="text-foreground">{t("auth.passwordResetSuccess")}</p>
              <Button asChild className="w-full">
                <Link to="/login">{t("auth.signIn")}</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={(e) => void submit(e)} className="space-y-3.5" noValidate>
              <Field
                id="new-password"
                label={t("auth.newPassword")}
                hint={t("auth.passwordPolicyHint")}
              >
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </Field>
              <Field id="confirm-password" label={t("auth.confirmPassword")}>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </Field>
              {error ? (
                <p role="alert" className="text-xs text-destructive">
                  {error}
                </p>
              ) : null}
              <Button
                type="submit"
                className="w-full"
                disabled={busy || !password || !confirmPassword}
              >
                {busy ? t("app.saving") : t("auth.setNewPassword")}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
